import { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
axios.defaults.withCredentials = true;
import {
  Mail, Lock, Eye, EyeOff, LogOut, ArrowLeft,
  Globe, Video, Megaphone, MessageCircle, Send,
  Paperclip, Loader2, RefreshCw, Mic, Square,
  Filter, CheckCircle2, Clock3, AlertCircle, Bell, Briefcase,
  Plus, X, Archive, Sun, Moon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:8888/crm_corona/api_assistenza";
const API = BACKEND_URL;
const OPERATOR_ATTACHMENT_ENDPOINT = `${API}/operator_upload_attachment.php`;
const APP_VERSION = "2.0.8 ";

const CLOUDINARY_CLOUD_NAME = "AppAssistenza";
const CLOUDINARY_VIDEO_UPLOAD_PRESET = "Preset_video";

const isStandalonePwa = () => {
  if (typeof window === "undefined") return false;

  const iosStandalone = window.navigator.standalone === true;
  const mediaStandalone = typeof window.matchMedia === "function"
    ? window.matchMedia("(display-mode: standalone)").matches
    : false;

  return iosStandalone || mediaStandalone;
};

const canUsePushNotifications = () => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    isStandalonePwa()
  );
};

const canRequestNotifications = () => {
  return canUsePushNotifications();
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const getPushPublicKey = async () => {
  const res = await axios.get(`${API}/operator_push_public_key.php`);
  const key = String(res?.data?.publicKey || "").trim();

  if (!res?.data?.success || !key) {
    throw new Error("Public key push non disponibile");
  }

  return key;
};

const registerOperatorPushSubscription = async () => {
  if (!canUsePushNotifications()) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  const existingSubscription = await registration.pushManager.getSubscription();

  if (existingSubscription) {
    await axios.post(`${API}/operator_save_push_subscription.php`, {
      subscription: existingSubscription.toJSON()
    });
    return existingSubscription;
  }

  const publicKey = await getPushPublicKey();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey)
  });

  await axios.post(`${API}/operator_save_push_subscription.php`, {
    subscription: subscription.toJSON()
  });

  return subscription;
};

const syncOperatorPushSubscriptionIfGranted = async () => {
  if (!canUsePushNotifications()) {
    return null;
  }

  if (Notification.permission !== "granted") {
    return null;
  }

  return registerOperatorPushSubscription();
};

const unregisterOperatorPushSubscription = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return;
    }

    const payload = { subscription: subscription.toJSON() };

    try {
      await axios.post(`${API}/operator_delete_push_subscription.php`, payload);
    } catch (_) {}

    try {
      await subscription.unsubscribe();
    } catch (_) {}
  } catch (_) {}
};

const setAppBadgeCount = async (count) => {
  const badgeCount = Math.max(0, Number(count || 0));

  try {
    if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
      return;
    }

    if (badgeCount > 0 && "setAppBadge" in navigator) {
      await navigator.setAppBadge(badgeCount);
    } else if ("clearAppBadge" in navigator) {
      await navigator.clearAppBadge();
    }
  } catch (_) {}

  try {
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: badgeCount > 0 ? "SET_BADGE" : "CLEAR_BADGE",
        count: badgeCount
      });
    }
  } catch (_) {}
};

const clearAppBadgeCount = async () => {
  await setAppBadgeCount(0);
};

const useServiceWorkerUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const refreshingRef = useRef(false);

  const trackInstallingWorker = useCallback((worker) => {
    if (!worker) return;

    worker.addEventListener("statechange", () => {
      if (
        worker.state === "installed" &&
        navigator.serviceWorker &&
        navigator.serviceWorker.controller
      ) {
        setWaitingWorker(worker);
        setUpdateAvailable(true);
      }
    });
  }, []);

  const bindRegistration = useCallback((registration) => {
    if (!registration) return;

    if (registration.waiting) {
      setWaitingWorker(registration.waiting);
      setUpdateAvailable(true);
    }

    if (registration.installing) {
      trackInstallingWorker(registration.installing);
    }

    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      trackInstallingWorker(newWorker);
    });
  }, [trackInstallingWorker]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    let mounted = true;

    const onControllerChange = () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const checkForUpdate = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration || !mounted) return;

        bindRegistration(registration);

        try {
          await registration.update();
        } catch (_) {}
      } catch (_) {}
    };

    checkForUpdate();

    const visibilityHandler = async () => {
      if (document.visibilityState === "visible") {
        await checkForUpdate();
      }
    };

    document.addEventListener("visibilitychange", visibilityHandler);

    const interval = setInterval(checkForUpdate, 60000);

    return () => {
      mounted = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visibilityHandler);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, [bindRegistration]);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) return;
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker]);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return {
    updateAvailable,
    applyUpdate,
    dismissUpdate
  };
};

const getAttachmentUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://unicosuite.cloud${url}`;
};

const isVideoFile = (file) => {
  if (!file) return false;

  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";

  if (type.startsWith("video/")) return true;
  return ["mp4", "mov", "webm"].includes(ext);
};

const isOperatorUser = (user) => {
  if (!user) return false;

  const aziendaId = Number(user.azienda_id ?? 0);
  const attiva = Number(user.attiva ?? 0);
  const ruolo = String(user.ruolo || "").toLowerCase();

  return aziendaId === 1 && attiva === 1 && (ruolo === "admin" || ruolo === "operatore");
};

const categoryLabels = {
  grafica: "Grafica",
  video: "Videomaking",
  meta_ads: "Social e Meta Ads",
  web_design: "Programmazione e Web",
  commerciale: "Commerciale e Amministrazione"
};

const statusLabels = {
  nuova: "Nuova",
  in_attesa: "In attesa",
  in_lavorazione: "In lavorazione",
  completata: "Completata"
};

const statusColors = {
  nuova: "#FF9500",
  in_attesa: "var(--status-attesa)",
  in_lavorazione: "var(--status-lavorazione)",
  completata: "var(--status-completata)"
};

const statusIcons = {
  nuova: AlertCircle,
  in_attesa: Clock3,
  in_lavorazione: RefreshCw,
  completata: CheckCircle2
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getMessageDayKey = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const capitalizeFirstLetter = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatMessageDayLabel = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const messageStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((todayStart - messageStart) / 86400000);

  if (diffDays === 0) return "Oggi";
  if (diffDays === 1) return "Ieri";

  const sameYear = today.getFullYear() === date.getFullYear();

  const label = date.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" })
  });

  return capitalizeFirstLetter(label);
};

const ChatDateSeparator = ({ label }) => {
  if (!label) return null;

  return (
    <div className="flex justify-center my-3">
      <span
        style={{
          background: "rgba(255,255,255,0.86)",
          color: "#54656f",
          borderRadius: 9999,
          padding: "6px 12px",
          fontSize: 12,
          fontWeight: 700,
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          border: "1px solid rgba(0,0,0,0.04)"
        }}
      >
        {label}
      </span>
    </div>
  );
};

const getMessageReadLabel = (msg) => {
  return Number(msg?.is_read ?? 0) === 1 ? "Letto" : "Inviato";
};

const getMessageAuthorLabel = (msg, isMine) => {
  if (isMine) return "";
  const explicitName = String(msg?.autore_nome || "").trim();
  if (explicitName) return explicitName;
  return msg?.mittente_tipo === "operatore" ? "Operatore" : "Cliente";
};

const SmallTag = ({
  children,
  icon = null,
  textColor = "#0b6bcb",
  borderColor = "#9fc5f8",
  bg = "#eaf3ff"
}) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: 22,
        padding: "0 8px",
        borderRadius: 7,
        border: `1px solid ${borderColor}`,
        background: bg,
        color: textColor,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        lineHeight: 1,
        whiteSpace: "nowrap",
        flexShrink: 0
      }}
    >
      {icon}
      <span>{children}</span>
    </span>
  );
};

const StatusTag = ({ status }) => {
  const statusKey = statusLabels[status] ? status : "in_lavorazione";
  const colorMap = {
    nuova: {
      textColor: "#b45309",
      borderColor: "#fdba74",
      bg: "#fff7ed"
    },
    in_attesa: {
      textColor: "#a16207",
      borderColor: "#f4cf74",
      bg: "#fff7df"
    },
    in_lavorazione: {
      textColor: "#0b6bcb",
      borderColor: "#9fc5f8",
      bg: "#eaf3ff"
    },
    completata: {
      textColor: "#15803d",
      borderColor: "#9ad9af",
      bg: "#eaf9ee"
    }
  };

  const Icon = statusIcons[statusKey] || RefreshCw;
  const colors = colorMap[statusKey];

  return (
    <SmallTag
      icon={<Icon className="w-3 h-3" />}
      textColor={colors.textColor}
      borderColor={colors.borderColor}
      bg={colors.bg}
    >
      {statusLabels[statusKey]}
    </SmallTag>
  );
};

const ServiceWorkerUpdateBanner = ({ onUpdate, onDismiss }) => {
  return (
    <div
      style={{
        position: "fixed",
        top: "calc(12px + env(safe-area-inset-top))",
        left: 12,
        right: 12,
        zIndex: 99999,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 680,
          background: "var(--chat-card)",
          border: "1px solid var(--border)",
          borderRadius: 18,
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
          padding: 14,
          pointerEvents: "auto"
        }}
      >
        <div className="flex items-start gap-3">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 9999,
              background: "rgba(37, 211, 102, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <RefreshCw className="w-5 h-5" style={{ color: "#25D366" }} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              È disponibile una nuova versione dell’app.
            </div>

            <div className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
              Aggiorna ora per usare subito l’ultima versione.
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={onUpdate}
                className="btn-primary flex items-center justify-center gap-2"
                style={{ minWidth: 150, padding: "10px 16px" }}
              >
                <RefreshCw className="w-4 h-4" />
                Aggiorna ora
              </button>

              <button
                type="button"
                onClick={onDismiss}
                style={{
                  height: 40,
                  padding: "0 14px",
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                  fontWeight: 700
                }}
              >
                Dopo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/operator_me.php`);
      const me = res.data;

      if (isOperatorUser(me)) {
        setUser(me);
      } else {
        setUser(null);
      }
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/operator_login.php`, { email, password });
    const loggedUser = res.data?.user ?? null;

    if (!isOperatorUser(loggedUser)) {
      throw {
        response: {
          data: {
            error: "Utente non autorizzato ad accedere all'app operatori"
          }
        }
      };
    }

    setUser(loggedUser);
    return loggedUser;
  };

  const logout = async () => {
    setUser(null);
    await clearAppBadgeCount();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, reloadUser: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Errore durante l'accesso"
      );
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "var(--background)" }}>
      <div className="w-full max-w-sm animate-fade-in">
        <div className="flex justify-center mb-8">
          <img
            src="/logo-unico.png"
            alt="Logo Corona"
            className="w-24 h-24 object-contain"
          />
        </div>

        <h1 className="text-2xl font-bold text-center mb-2">Operatori Assistenza</h1>
        <p className="text-center mb-8" style={{ color: "var(--text-secondary)" }}>
          Accedi come operatore Corona Consulting
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-field"
              style={{ paddingLeft: "48px" }}
              required
              data-testid="login-email"
            />
            <Mail
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
              style={{ color: "var(--text-muted)" }}
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input-field"
              style={{ paddingLeft: "48px", paddingRight: "48px" }}
              required
              data-testid="login-password"
            />
            <Lock
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
              style={{ color: "var(--text-muted)" }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-muted)" }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {error && (
            <p className="text-sm text-center" style={{ color: "#FF3B30" }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
          </button>
        </form>

        <div
          className="mt-6 text-center text-xs"
          style={{ color: "var(--text-muted)" }}
        >
          Operatori Assistenza v{APP_VERSION}
        </div>
      </div>
    </div>
  );
};

const CategoryIcon = ({ category, size = 24 }) => {
  const icons = {
    grafica: { icon: Megaphone, color: "var(--cat-grafica, #7c3aed)", bg: "rgba(124, 58, 237, 0.15)" },
    video: { icon: Video, color: "var(--cat-video)", bg: "rgba(255, 59, 48, 0.15)" },
    meta_ads: { icon: Megaphone, color: "var(--cat-ads)", bg: "rgba(175, 82, 222, 0.15)" },
    web_design: { icon: Globe, color: "var(--cat-web)", bg: "rgba(0, 122, 255, 0.15)" },
    commerciale: { icon: Briefcase, color: "var(--cat-commerciale, #f59e0b)", bg: "rgba(245, 158, 11, 0.15)" }
  };
  const config = icons[category] || icons.web_design;
  const Icon = config.icon;

  return (
    <div
      style={{
        width: size * 2,
        height: size * 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 9999,
        background: config.bg,
        flexShrink: 0
      }}
    >
      <Icon
        style={{
          color: config.color,
          width: size,
          height: size,
          display: "block",
          flexShrink: 0
        }}
      />
    </div>
  );
};

const RequestCard = ({ request, onClick, unreadCount = 0, archived = false }) => {
  const referenceDate = request.completata_at || request.updated_at || request.created_at;
  const createdTime = formatTime(referenceDate);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left"
      data-testid={`request-card-${request.id}`}
      style={{ background: "transparent" }}
    >
      <div
        className="flex items-start gap-2 px-3 py-2"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div
          className="shrink-0"
          style={{
            width: 44,
            height: 36,
            borderRadius: 9999,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0
          }}
        >
          <CategoryIcon category={request.categoria} size={14} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2 flex-wrap">
                <h3
                  className="font-semibold text-sm truncate"
                  style={{ lineHeight: "20px", marginTop: 1 }}
                >
                  {request.titolo}
                </h3>

                <SmallTag
                  icon={
                    request.categoria === "grafica" ? <Megaphone className="w-3 h-3" /> :
                    request.categoria === "video" ? <Video className="w-3 h-3" /> :
                    request.categoria === "meta_ads" ? <Megaphone className="w-3 h-3" /> :
                    request.categoria === "web_design" ? <Globe className="w-3 h-3" /> :
                    request.categoria === "commerciale" ? <Briefcase className="w-3 h-3" /> :
                    <MessageCircle className="w-3 h-3" />
                  }
                >
                  {categoryLabels[request.categoria] || request.categoria}
                </SmallTag>

                {archived && (
                  <SmallTag
                    icon={<Archive className="w-3 h-3" />}
                    textColor="#6b7280"
                    borderColor="#d1d5db"
                    bg="#f3f4f6"
                  >
                    Archivio
                  </SmallTag>
                )}
              </div>

              <div className="flex items-center gap-2 mt-1 min-w-0 flex-wrap">
                <span className="truncate text-sm" style={{ color: "var(--text-secondary)" }}>
                  {request.cliente_nome ? `Cliente: ${request.cliente_nome}` : "Cliente non disponibile"}
                </span>
              </div>

              <div className="flex items-center gap-2 mt-2 min-w-0 flex-wrap">
                <span className="truncate text-sm" style={{ color: "var(--text-secondary)" }}>
                  {request.operatore_nome ? `Assegnata a: ${request.operatore_nome}` : "Non assegnata"}
                </span>

                <StatusTag status={request.stato} />
              </div>
            </div>

            <div className="shrink-0 flex flex-col items-end gap-2">
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                {createdTime}
              </span>

              {!archived && unreadCount > 0 && (
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    minWidth: 20,
                    height: 20,
                    padding: "0 8px",
                    borderRadius: 9999,
                    background: "var(--primary)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: "20px"
                  }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

const LinkifiedText = ({ text }) => {
  const value = String(text || "");
  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = value.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        regex.lastIndex = 0;

        if (regex.test(part)) {
          regex.lastIndex = 0;

          const href = part.toLowerCase().startsWith("www.")
            ? `https://${part}`
            : part;

          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }

        regex.lastIndex = 0;
        return <span key={index}>{part}</span>;
      })}
    </>
  );
};

const ChatBubble = ({ msg, isMine }) => {
  const isCustomerMessage = msg?.mittente_tipo === "cliente";
  const isOtherOperatorMessage = msg?.mittente_tipo === "operatore" && !isMine;
  const authorLabel = getMessageAuthorLabel(msg, isMine);

  const bubbleBaseStyle = isMine
    ? {
        background: "linear-gradient(180deg, #25D366 0%, #22c55e 100%)",
        color: "#ffffff",
        borderTopRightRadius: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
      }
    : isOtherOperatorMessage
      ? {
          background: "#dbeafe",
          color: "var(--text-primary)",
          borderTopLeftRadius: 8,
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
        }
      : {
          background: "var(--chat-card)",
          color: "var(--text-primary)",
          borderTopLeftRadius: 8,
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)"
        };

  const bubbleStyle = msg.tipo === "audio"
    ? {
        ...bubbleBaseStyle,
        width: "min(360px, calc(100vw - 48px))",
        maxWidth: "min(360px, calc(100vw - 48px))",
        padding: "8px 10px",
        borderRadius: 18
      }
    : {
        ...bubbleBaseStyle,
        maxWidth: "84%",
        padding: "8px 10px",
        borderRadius: 18
      };

  const messageStatusLabel = isMine ? getMessageReadLabel(msg) : "";
  const statusColor = Number(msg?.is_read ?? 0) === 1 ? "#d1fae5" : "rgba(255,255,255,0.92)";
  const metaOpacity = isMine ? 0.92 : 0.72;
  const authorColor = isOtherOperatorMessage ? "#1d4ed8" : isCustomerMessage ? "#475467" : "#ffffff";

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div style={bubbleStyle}>
        {msg.allegato_url && (
          <div className={msg.tipo === "audio" ? "mb-1" : "mb-2"}>
            {msg.tipo === "immagine" ? (
              <a
                href={getAttachmentUrl(msg.allegato_url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={getAttachmentUrl(msg.allegato_url)}
                  alt={msg.allegato_nome}
                  className="max-w-full rounded-2xl max-h-60 object-cover cursor-pointer"
                />
              </a>
            ) : msg.tipo === "video" ? (
              <div className="overflow-hidden rounded-2xl">
                <video
                  src={getAttachmentUrl(msg.allegato_url)}
                  controls
                  playsInline
                  className="max-w-full max-h-72 block bg-black"
                />
              </div>
            ) : msg.tipo === "audio" ? (
              <div
                style={{
                  width: "100%",
                  background: "var(--chat-card)",
                  borderRadius: 10,
                  padding: "8px",
                  display: "block",
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)"
                }}
              >
                <audio
                  key={`${msg.id}-${msg.allegato_url}`}
                  controls
                  preload="auto"
                  style={{
                    width: "100%",
                    height: 36,
                    display: "block"
                  }}
                  src={getAttachmentUrl(msg.allegato_url)}
                />
              </div>
            ) : (
              <a
                href={getAttachmentUrl(msg.allegato_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 underline"
              >
                <Paperclip className="w-4 h-4" />
                {msg.allegato_nome}
              </a>
            )}
          </div>
        )}

        {msg.tipo !== "audio" && (
          <p
            className="text-sm leading-[1.35] whitespace-pre-wrap break-words"
            style={{ paddingRight: 42 }}
          >
            <LinkifiedText text={msg.contenuto} />
          </p>
        )}

        <div
          className="text-[11px] flex justify-end mt-1 items-center gap-1.5 flex-wrap"
          style={{ opacity: metaOpacity }}
        >
          {!isMine && authorLabel && (
            <>
              <span style={{ color: authorColor, fontWeight: 700 }}>
                {authorLabel}
              </span>
              <span>·</span>
            </>
          )}

          <span>{formatTime(msg.created_at)}</span>

          {isMine && (
            <>
              <span>·</span>
              <span style={{ color: statusColor, fontWeight: 600 }}>
                {messageStatusLabel}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SelectField = ({ value, onChange, options, disabled = false }) => {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full"
      style={{
        height: 42,
        borderRadius: 12,
        border: "1px solid var(--border)",
        background: "var(--surface)",
        padding: "0 12px",
        fontSize: 12,
        outline: "none",
        color: "var(--text-primary)"
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
};

const TextField = ({ value, onChange, placeholder = "", type = "text" }) => {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full"
      style={{
        height: 42,
        borderRadius: 12,
        border: "1px solid var(--border)",
        background: "var(--surface)",
        padding: "0 12px",
        fontSize: 12,
        outline: "none",
        color: "var(--text-primary)"
      }}
    />
  );
};

const TextAreaField = ({ value, onChange, placeholder = "", rows = 5 }) => {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full"
      style={{
        borderRadius: 12,
        border: "1px solid var(--border)",
        background: "var(--surface)",
        padding: "12px",
        fontSize: 14,
        outline: "none",
        color: "var(--text-primary)",
        resize: "none"
      }}
    />
  );
};

const NewRequestModal = ({
  open,
  onClose,
  onCreated
}) => {
  const [clienti, setClienti] = useState([]);
  const [clientiLoading, setClientiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    utente_id: "",
    categoria: "web_design",
    titolo: "",
    messaggio: ""
  });

  const loadClienti = useCallback(async () => {
    setClientiLoading(true);
    setError("");

    try {
      const res = await axios.get(`${API}/operator_clients.php`);
      const data = Array.isArray(res.data) ? res.data : [];
      setClienti(data);
    } catch (err) {
      console.error(err);
      setClienti([]);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Errore durante il caricamento dei clienti"
      );
    }

    setClientiLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      loadClienti();
    }
  }, [open, loadClienti]);

  useEffect(() => {
    if (!open) {
      setError("");
      setSaving(false);
      setForm({
        utente_id: "",
        categoria: "web_design",
        titolo: "",
        messaggio: ""
      });
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.utente_id || !form.categoria || !form.titolo.trim() || !form.messaggio.trim()) {
      setError("Compila tutti i campi");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const res = await axios.post(`${API}/operator_create_chat.php`, {
        utente_id: Number(form.utente_id),
        categoria: form.categoria,
        titolo: form.titolo.trim(),
        messaggio: form.messaggio.trim()
      });

      if (!res?.data?.success || !res?.data?.richiesta_id) {
        throw new Error(res?.data?.message || "Errore durante la creazione della richiesta");
      }

      if (typeof onCreated === "function") {
        onCreated(Number(res.data.richiesta_id));
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Errore durante la creazione della richiesta"
      );
    }

    setSaving(false);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.48)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center"
      }}
    >
      <div
        className="w-full max-w-2xl rounded-t-[28px]"
        style={{
          background: "var(--background)",
          maxHeight: "92dvh",
          overflow: "hidden",
          boxShadow: "0 -10px 30px rgba(0,0,0,0.16)"
        }}
      >
        <div
          className="sticky top-0 z-10 px-4 py-4 flex items-center justify-between"
          style={{
            background: "var(--background)",
            borderBottom: "1px solid var(--border)"
          }}
        >
          <div>
            <h2 className="text-lg font-bold">Nuova richiesta</h2>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Crea una nuova richiesta per un cliente
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full"
            style={{ background: "var(--surface)" }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4" style={{ maxHeight: "calc(92dvh - 82px)" }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Cliente</label>
              <select
                value={form.utente_id}
                onChange={(e) => setForm((prev) => ({ ...prev, utente_id: e.target.value }))}
                disabled={clientiLoading || saving}
                className="w-full"
                style={{
                  height: 42,
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  padding: "0 12px",
                  fontSize: 14,
                  outline: "none",
                  color: "var(--text-primary)"
                }}
              >
                <option value="">
                  {clientiLoading ? "Caricamento clienti..." : "Seleziona cliente"}
                </option>
                {clienti.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Categoria</label>
              <SelectField
                value={form.categoria}
                onChange={(e) => setForm((prev) => ({ ...prev, categoria: e.target.value }))}
                disabled={saving}
                options={[
                  { value: "grafica", label: "Grafica" },
                  { value: "video", label: "Videomaking" },
                  { value: "meta_ads", label: "Social e Meta Ads" },
                  { value: "web_design", label: "Programmazione e Web" },
                  { value: "commerciale", label: "Commerciale e Amministrazione" }
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Titolo richiesta</label>
              <TextField
                value={form.titolo}
                onChange={(e) => setForm((prev) => ({ ...prev, titolo: e.target.value }))}
                placeholder="Inserisci il titolo della richiesta"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Messaggio iniziale</label>
              <TextAreaField
                value={form.messaggio}
                onChange={(e) => setForm((prev) => ({ ...prev, messaggio: e.target.value }))}
                placeholder="Scrivi il primo messaggio"
                rows={6}
              />
            </div>

            {error && (
              <div
                className="rounded-2xl px-4 py-3 text-sm font-medium"
                style={{
                  background: "rgba(255,59,48,0.08)",
                  border: "1px solid rgba(255,59,48,0.16)",
                  color: "#FF3B30"
                }}
              >
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1"
                style={{
                  height: 48,
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                  fontWeight: 700
                }}
              >
                Annulla
              </button>

              <button
                type="submit"
                disabled={saving || clientiLoading}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                style={{ height: 48, borderRadius: 14 }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crea richiesta
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ChatView = ({ chatId, onBack, archived = false }) => {
  const { user } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordingError, setRecordingError] = useState("");
  const [takingCharge, setTakingCharge] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const currentRecordingMimeTypeRef = useRef("");
  const isStoppingRecordingRef = useRef(false);

  const chatsEndpoint = archived ? `${API}/operator_chats_archive.php` : `${API}/operator_chats.php`;
  const messagesEndpoint = archived ? `${API}/operator_messages_archive.php` : `${API}/operator_messages.php`;

  const canTakeCharge = useMemo(() => {
    if (archived) return false;
    if (!chat || !user) return false;
    if (!isOperatorUser(user)) return false;

    const operatoreId = Number(chat.operatore_id ?? 0);
    return !operatoreId;
  }, [chat, user, archived]);

  const canChangeStatus = useMemo(() => {
    if (archived) return false;
    if (!chat || !user) return false;
    if (!isOperatorUser(user)) return false;

    const operatoreId = Number(chat.operatore_id ?? 0);
    return operatoreId === 0 || operatoreId === Number(user.id);
  }, [chat, user, archived]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior
        });
      }
    });
  }, []);

  const stopMediaTracks = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
      mediaStreamRef.current = null;
    }
  }, []);

  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const resetRecorderState = useCallback(() => {
    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    currentRecordingMimeTypeRef.current = "";
    isStoppingRecordingRef.current = false;
  }, []);

  const getSupportedAudioMimeType = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
      "audio/ogg"
    ];

    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      return "";
    }

    for (const mime of candidates) {
      try {
        if (window.MediaRecorder.isTypeSupported(mime)) {
          return mime;
        }
      } catch (_) {}
    }

    return "";
  };

  const getAudioExtensionByMimeType = (mimeType) => {
    const normalized = String(mimeType || "").toLowerCase();

    if (!normalized) return "webm";
    if (normalized.includes("webm")) return "webm";
    if (normalized.includes("ogg")) return "ogg";
    if (normalized.includes("mp4")) return "m4a";
    if (normalized.includes("mpeg")) return "mp3";
    if (normalized.includes("wav")) return "wav";

    return "webm";
  };

  const uploadVideoToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_VIDEO_UPLOAD_PRESET);
    formData.append("folder", `appassistenza/chat_${chatId}`);

    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/video/upload`,
      formData,
      {
        withCredentials: false
      }
    );

    if (!response?.data?.secure_url || !response?.data?.public_id) {
      throw new Error("Risposta Cloudinary non valida");
    }

    return {
      public_id: response.data.public_id,
      secure_url: response.data.secure_url
    };
  };

  const saveCloudinaryVideo = async (file, cloudinaryData) => {
    await axios.post(`${API}/save_cloudinary_video.php`, {
      user_id: user.id,
      chat_id: chatId,
      contenuto: file.name,
      nome_originale: file.name,
      cloudinary_public_id: cloudinaryData.public_id,
      cloudinary_url: cloudinaryData.secure_url,
      mime_type: file.type || "video/mp4",
      dimensione: file.size || 0
    });
  };

  const playNotificationSound = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      const ctx = new AudioContextClass();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = "sine";
      oscillator.frequency.value = 880;

      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.21);

      oscillator.onended = () => {
        try { ctx.close(); } catch (_) {}
      };
    } catch (_) {}
  }, []);

  const loadChat = useCallback(async () => {
    try {
      const chatRes = await axios.get(`${chatsEndpoint}?id=${chatId}`);
      setChat(chatRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [chatId, chatsEndpoint]);

  const loadMessages = useCallback(async (markRead = true) => {
    try {
      const messagesRes = await axios.get(`${messagesEndpoint}?chat_id=${chatId}`);
      const newMessages = Array.isArray(messagesRes.data) ? messagesRes.data : [];
      setMessages(newMessages);

      if (newMessages.length > 0) {
        const lastMsg = newMessages[newMessages.length - 1];
        const lastId = Number(lastMsg?.id ?? 0);

        if (lastMessageIdRef.current === 0) {
          lastMessageIdRef.current = lastId;
        } else if (lastId > lastMessageIdRef.current) {
          lastMessageIdRef.current = lastId;

          if (!archived && Number(lastMsg?.sender_id ?? 0) !== Number(user?.id ?? 0)) {
            playNotificationSound();
          }
        }
      }

      if (!archived && markRead && user?.id) {
        await axios.post(`${API}/operator_read.php`, {
          chat_id: chatId
        });
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [chatId, user, playNotificationSound, archived, messagesEndpoint]);

  const loadData = useCallback(async (markRead = true) => {
    await Promise.all([
      loadChat(),
      loadMessages(markRead)
    ]);
  }, [loadChat, loadMessages]);

  useEffect(() => {
    lastMessageIdRef.current = 0;
    loadData();
    const interval = setInterval(() => loadMessages(false), archived ? 15000 : 10000);

    return () => {
      clearInterval(interval);
      stopRecordingTimer();
      stopMediaTracks();

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }

      resetRecorderState();
    };
  }, [loadData, loadMessages, stopMediaTracks, stopRecordingTimer, resetRecorderState, archived]);

  useEffect(() => {
    if (!loading) {
      scrollToBottom(messages.length <= 1 ? "auto" : "smooth");
    }
  }, [messages, loading, scrollToBottom]);

  const sendMessage = async () => {
    if (archived || !newMessage.trim() || sending || recording) return;

    setSending(true);
    try {
      await axios.post(`${API}/operator_messages.php?chat_id=${chatId}`, {
        contenuto: newMessage
      });
      setNewMessage("");
      await loadMessages(false);
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  const uploadAudioBlob = async (blob, mimeType) => {
    const finalMimeType = String(mimeType || blob?.type || "audio/webm").toLowerCase();
    const extension = getAudioExtensionByMimeType(finalMimeType);
    const fileName = `vocale_${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`;
    const file = new File([blob], fileName, {
      type: finalMimeType || "audio/webm"
    });

    if (!file || file.size <= 0) {
      throw new Error("File audio non valido");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", user.id);
    formData.append("contenuto", "🎤 Vocale");
    formData.append("chat_id", chatId);

    await axios.post(OPERATOR_ATTACHMENT_ENDPOINT, formData);
  };

  const startRecording = async () => {
    if (archived || sending || recording) return;

    setRecordingError("");

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setRecordingError("Il dispositivo non supporta la registrazione audio");
      return;
    }

    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      setRecordingError("Il browser non supporta i vocali");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      mediaStreamRef.current = stream;

      const mimeType = getSupportedAudioMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recordingChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      currentRecordingMimeTypeRef.current = mimeType || mediaRecorder.mimeType || "";
      isStoppingRecordingRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event?.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        setRecordingError("Errore durante la registrazione del vocale");
        setRecording(false);
        stopRecordingTimer();
        stopMediaTracks();
        resetRecorderState();
      };

      mediaRecorder.onstop = async () => {
        stopRecordingTimer();
        setRecording(false);

        const chunks = [...recordingChunksRef.current];
        const finalMimeType =
          currentRecordingMimeTypeRef.current ||
          mediaRecorder.mimeType ||
          chunks?.[0]?.type ||
          "audio/webm";

        const audioBlob = new Blob(chunks, {
          type: finalMimeType
        });

        stopMediaTracks();
        resetRecorderState();

        if (!audioBlob || audioBlob.size <= 0) {
          setRecordingError("Registrazione non valida");
          return;
        }

        setSending(true);
        try {
          await uploadAudioBlob(audioBlob, finalMimeType);
          setRecordingSeconds(0);
          await loadMessages(false);
        } catch (err) {
          console.error(err);
          setRecordingError("Errore durante l'invio del vocale");
        }
        setSending(false);
      };

      mediaRecorder.start(250);
      setRecording(true);
      setRecordingSeconds(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error(err);
      setRecordingError("Microfono non disponibile o permesso negato");
      stopRecordingTimer();
      stopMediaTracks();
      resetRecorderState();
      setRecording(false);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === "inactive" || isStoppingRecordingRef.current) {
      return;
    }

    isStoppingRecordingRef.current = true;

    try {
      if (recorder.state === "recording") {
        try {
          recorder.requestData();
        } catch (_) {}

        setTimeout(() => {
          try {
            if (recorder.state !== "inactive") {
              recorder.stop();
            }
          } catch (err) {
            console.error(err);
            setRecording(false);
            stopRecordingTimer();
            stopMediaTracks();
            resetRecorderState();
            setRecordingError("Errore durante la chiusura della registrazione");
          }
        }, 150);
      }
    } catch (err) {
      console.error(err);
      setRecording(false);
      stopRecordingTimer();
      stopMediaTracks();
      resetRecorderState();
      setRecordingError("Errore durante la chiusura della registrazione");
    }
  };

  const toggleRecording = async () => {
    if (archived) return;

    if (recording) {
      stopRecording();
    } else {
      await startRecording();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || archived) return;

    setSending(true);
try {
  if (isVideoFile(file)) {
    const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

    if (file.size > MAX_VIDEO_SIZE) {
      throw new Error("Il video supera il limite massimo di 100 MB");
    }

    const cloudinaryData = await uploadVideoToCloudinary(file);
    await saveCloudinaryVideo(file, cloudinaryData);
  } else {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", user.id);
    formData.append("contenuto", file.name);
    formData.append("chat_id", chatId);

    await axios.post(OPERATOR_ATTACHMENT_ENDPOINT, formData);
  }

      await loadMessages(false);
    } catch (err) {
      console.error(err);
    }
    setSending(false);
    e.target.value = "";
  };

  const handleTakeCharge = async () => {
    if (archived || !user?.id || !canTakeCharge || takingCharge) return;

    setTakingCharge(true);
    try {
      await axios.post(`${API}/operator_take_chat.php`, {
        chat_id: chatId
      });
      await loadData(false);
    } catch (err) {
      console.error(err);
    }
    setTakingCharge(false);
  };

  const handleStatusChange = async (e) => {
    const nextStatus = e.target.value;
    if (archived || !nextStatus || !canChangeStatus || updatingStatus) return;

    setUpdatingStatus(true);
    try {
      await axios.post(`${API}/operator_update_status.php`, {
        chat_id: chatId,
        stato: nextStatus
      });
      await loadChat();
    } catch (err) {
      console.error(err);
    }
    setUpdatingStatus(false);
  };

  const formatRecordingTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const currentStatusColor = statusColors[chat?.stato] || "var(--status-lavorazione)";

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
      </div>
    );
  }

  return (
    <div
      className="h-[100dvh] flex flex-col overflow-hidden"
      style={{
        backgroundColor: "var(--chat-bg)",
        backgroundImage:
          "radial-gradient(rgba(0,0,0,0.03) 0.8px, transparent 0.8px)",
        backgroundSize: "18px 18px"
      }}
    >
      <div
        className="shrink-0 z-10 border-b"
        style={{
          borderColor: "rgba(0,0,0,0.06)",
          background: "var(--chat-panel)",
          paddingTop: "env(safe-area-inset-top)"
        }}
      >
        <div className="flex items-center gap-3 px-4 py-2">
          <button onClick={onBack} className="p-1" data-testid="chat-back">
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div
            className="shrink-0 flex items-center justify-center rounded-full"
            style={{
              width: 40,
              height: 40,
              background: "var(--chat-avatar-bg)",
              color: "var(--chat-muted)"
            }}
          >
            <MessageCircle className="w-5 h-5" />
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="font-semibold truncate">{chat?.titolo}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: currentStatusColor }}
              />
              <span className="text-sm truncate" style={{ color: "var(--chat-muted)" }}>
                {statusLabels[chat?.stato] || "In lavorazione"}
              </span>
              {chat?.cliente_nome && (
                <span className="text-sm truncate" style={{ color: "var(--chat-muted)" }}>
                  · {chat.cliente_nome}
                </span>
              )}

              {archived && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
                  style={{
                    background: "#f3f4f6",
                    color: "var(--text-muted)",
                    border: "1px solid #d1d5db"
                  }}
                >
                  <Archive className="w-3 h-3" />
                  Sola lettura
                </span>
              )}
            </div>
          </div>

          <button onClick={() => loadData(true)} className="p-2" data-testid="refresh-chat">
            <RefreshCw className="w-5 h-5" style={{ color: "var(--chat-muted)" }} />
          </button>
        </div>

        <div className="px-3 pb-2">
          <div
            className="rounded-xl px-3 py-2"
            style={{
              background: "var(--chat-card)",
              border: "1px solid rgba(0,0,0,0.06)"
            }}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CategoryIcon category={chat?.categoria} size={14} />
                  <SmallTag
                    icon={
                      chat?.categoria === "grafica" ? <Megaphone className="w-3 h-3" /> :
                      chat?.categoria === "video" ? <Video className="w-3 h-3" /> :
                      chat?.categoria === "meta_ads" ? <Megaphone className="w-3 h-3" /> :
                      chat?.categoria === "web_design" ? <Globe className="w-3 h-3" /> :
                      chat?.categoria === "commerciale" ? <Briefcase className="w-3 h-3" /> :
                      <MessageCircle className="w-3 h-3" />
                    }
                  >
                    {categoryLabels[chat?.categoria] || chat?.categoria}
                  </SmallTag>
                  <StatusTag status={chat?.stato} />
                </div>

                 {archived && chat?.completata_at && (
                  <div className="mt-1 text-sm" style={{ color: "var(--chat-muted)" }}>
                    Archiviata il {formatDateTime(chat.completata_at)}
                  </div>
                )}
              </div>

              {canTakeCharge && (
                <button
                  type="button"
                  onClick={handleTakeCharge}
                  disabled={takingCharge}
                  className="btn-primary flex items-center justify-center gap-2"
                  style={{ minWidth: 160 }}
                >
                  {takingCharge ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Prendi in carico
                </button>
              )}
            </div>

            <div className="mt-2">
              <label className="block text-xs font-semibold mb-1" style={{ color: "var(--chat-muted)" }}>
                Stato richiesta
              </label>
              <SelectField
                value={chat?.stato || "nuova"}
                onChange={handleStatusChange}
                disabled={!canChangeStatus || updatingStatus}
                options={[
                  { value: "nuova", label: "Nuova" },
                  { value: "in_attesa", label: "In attesa" },
                  { value: "in_lavorazione", label: "In lavorazione" },
                  { value: "completata", label: "Completata" }
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2"
      >
        {messages.map((msg, index) => {
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const currentDayKey = getMessageDayKey(msg.created_at);
          const previousDayKey = previousMessage ? getMessageDayKey(previousMessage.created_at) : "";
          const showDaySeparator = currentDayKey && currentDayKey !== previousDayKey;

          return (
            <div key={msg.id || `message-${index}`}>
              {showDaySeparator && (
                <ChatDateSeparator label={formatMessageDayLabel(msg.created_at)} />
              )}

              <ChatBubble
                msg={msg}
                isMine={Number(msg.sender_id) === Number(user?.id)}
              />
            </div>
          );
        })}
      </div>

      <div
        className="shrink-0 border-t"
        style={{
          borderColor: "rgba(0,0,0,0.05)",
          background: "var(--chat-panel)",
          paddingBottom: `calc(10px + env(safe-area-inset-bottom))`
        }}
      >
        {archived && (
          <div className="px-3 pt-3">
            <div
              className="rounded-[24px] px-4 py-4 text-sm"
              style={{
                background: "var(--chat-card)",
                color: "var(--chat-muted)",
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)"
              }}
            >
              Questa richiesta è archiviata. Puoi consultare messaggi e allegati, ma non puoi inviare nuove risposte.
            </div>
          </div>
        )}

        {recordingError && !archived && (
          <div className="px-4 pt-3 text-sm" style={{ color: "#FF3B30" }}>
            {recordingError}
          </div>
        )}

        {!archived && recording && (
          <div
            className="mx-3 mt-3 flex items-center justify-between rounded-2xl px-4 py-3"
            style={{
              background: "rgba(255,59,48,0.08)",
              border: "1px solid rgba(255,59,48,0.16)"
            }}
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-block w-3 h-3 rounded-full animate-pulse"
                style={{ background: "#FF3B30" }}
              />
              <span className="text-sm font-medium">Registrazione in corso</span>
            </div>
            <span className="text-sm font-semibold">{formatRecordingTime(recordingSeconds)}</span>
          </div>
        )}

        <div className="px-3 pt-3">
          <div
            className="flex items-center gap-2 rounded-[28px] px-2 py-2"
            style={{
              background: "var(--chat-panel)"
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              id="file-upload"
              className="hidden"
              onChange={handleFileUpload}
              accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/webm,.pdf,audio/*,audio/webm,audio/ogg,audio/mp4,audio/x-m4a,audio/m4a,audio/mpeg,audio/aac,audio/x-aac,audio/wav,audio/x-wav,.m4a,.mp3,.ogg,.wav,.aac,.caf,.amr,.3gp,.3gpp"
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 flex items-center justify-center rounded-full"
              data-testid="attach-file"
              disabled={archived || sending || recording}
              style={{
                width: 42,
                height: 42,
                color: archived || sending || recording ? "#a8b0b6" : "#54656f"
              }}
            >
              <Paperclip className="w-5 h-5" />
            </button>

            <button
              onClick={toggleRecording}
              disabled={archived || sending}
              className="shrink-0 flex items-center justify-center rounded-full"
              data-testid="record-audio"
              style={{
                width: 42,
                height: 42,
                color: recording ? "#FF3B30" : "#54656f"
              }}
            >
              {recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            <div
              className="flex-1 flex items-center rounded-[24px] px-4"
              style={{
                background: "var(--chat-card)",
                minHeight: 48,
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)"
              }}
            >
              <input
                type="text"
                placeholder={archived ? "Richiesta archiviata" : recording ? "Registrazione in corso..." : "Scrivi un messaggio"}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                className="w-full bg-transparent outline-none text-sm chat-text-input"
                data-testid="chat-input"
                disabled={archived || recording}
                style={{ color: "var(--text-primary)" }}
              />
            </div>

            <button
              onClick={sendMessage}
              disabled={archived || sending || !newMessage.trim() || recording}
              className="shrink-0 flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 36,
                background: archived || sending || !newMessage.trim() || recording ? "#d7dce0" : "#25D366",
                color: archived || sending || !newMessage.trim() || recording ? "#6b7280" : "#ffffff",
                transition: "all 0.2s ease"
              }}
              data-testid="send-message"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainApp = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [archivedRequests, setArchivedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [assignmentFilter, setAssignmentFilter] = useState("default");
  const [statusFilter, setStatusFilter] = useState("tutte");
  const [activeTab, setActiveTab] = useState("attive");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem("operator_theme") || "light";
    } catch (_) {
      return "light";
    }
  });
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [requestingNotificationPermission, setRequestingNotificationPermission] = useState(false);
  const syncingPushSubscriptionRef = useRef(false);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);

    try {
      localStorage.setItem("operator_theme", themeMode);
    } catch (_) {}
  }, [themeMode]);

  const toggleThemeMode = useCallback(() => {
    setThemeMode((current) => current === "dark" ? "light" : "dark");
  }, []);

  const updateBadgeFromRequests = useCallback(async (items) => {
    const totalUnread = (Array.isArray(items) ? items : []).reduce((sum, item) => {
      return sum + Number(item?.non_letti_operatore ?? 0);
    }, 0);

    await setAppBadgeCount(totalUnread);
  }, []);

  const buildActiveChatParams = useCallback(() => {
    const params = new URLSearchParams();

    if (assignmentFilter !== "default") {
      params.append("assignment", assignmentFilter);
    }

    if (statusFilter !== "tutte") {
      params.append("status", statusFilter);
    }


    return params.toString();
  }, [assignmentFilter, statusFilter]);

  const buildArchiveChatParams = useCallback(() => {
    const params = new URLSearchParams();
    params.append("status", "completata");


    return params.toString();
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const activeQueryString = buildActiveChatParams();
      const archiveQueryString = buildArchiveChatParams();

      const activeUrl = activeQueryString
        ? `${API}/operator_chats.php?${activeQueryString}`
        : `${API}/operator_chats.php`;

      const archiveUrl = archiveQueryString
        ? `${API}/operator_chats_archive.php?${archiveQueryString}`
        : `${API}/operator_chats_archive.php`;

      const [activeRes, archiveRes] = await Promise.all([
        axios.get(activeUrl),
        axios.get(archiveUrl)
      ]);

      const activeData = Array.isArray(activeRes.data) ? activeRes.data : [];
      const archiveData = Array.isArray(archiveRes.data) ? archiveRes.data : [];

      setRequests(activeData);
      setArchivedRequests(archiveData);
      await updateBadgeFromRequests(activeData);
    } catch (err) {
      console.error(err);
      setRequests([]);
      setArchivedRequests([]);
      await clearAppBadgeCount();
    }

    setLoading(false);
  }, [buildActiveChatParams, buildArchiveChatParams, updateBadgeFromRequests]);

  const syncPushSubscription = useCallback(async () => {
    if (!canUsePushNotifications() || syncingPushSubscriptionRef.current) {
      return;
    }

    if (Notification.permission !== "granted") {
      return;
    }

    syncingPushSubscriptionRef.current = true;

    try {
      await syncOperatorPushSubscriptionIfGranted();
    } catch (err) {
      console.error(err);
    } finally {
      syncingPushSubscriptionRef.current = false;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const chatIdFromUrl = Number(params.get("chat_id") || 0);
    const archivedFromUrl = params.get("archivio") === "1";

    if (chatIdFromUrl > 0) {
      setSelectedChat({ id: chatIdFromUrl, archived: archivedFromUrl });
      setActiveTab(archivedFromUrl ? "archivio" : "attive");
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    const requestsInterval = setInterval(loadRequests, 5000);

    return () => {
      clearInterval(requestsInterval);
    };
  }, [loadRequests]);

  useEffect(() => {
    if (user && notificationPermission === "granted") {
      syncPushSubscription();
    }
  }, [user, notificationPermission, syncPushSubscription]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        if (typeof Notification !== "undefined") {
          setNotificationPermission(Notification.permission);
        }
        await loadRequests();
        await syncPushSubscription();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadRequests, syncPushSubscription]);

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const chatIdFromUrl = Number(params.get("chat_id") || 0);
      const archivedFromUrl = params.get("archivio") === "1";

      setSelectedChat(chatIdFromUrl > 0 ? { id: chatIdFromUrl, archived: archivedFromUrl } : null);
      setActiveTab(archivedFromUrl ? "archivio" : "attive");
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const requestNotificationPermission = async () => {
    if (!canRequestNotifications() || requestingNotificationPermission) {
      return;
    }

    setRequestingNotificationPermission(true);

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        await syncPushSubscription();
        await loadRequests();
      }
    } catch (err) {
      console.error(err);
    }

    setRequestingNotificationPermission(false);
  };

  const openChat = (chatId, archived = false) => {
    setSelectedChat({ id: chatId, archived });

    const url = new URL(window.location.href);
    url.searchParams.set("chat_id", String(chatId));

    if (archived) {
      url.searchParams.set("archivio", "1");
    } else {
      url.searchParams.delete("archivio");
    }

    window.history.pushState({}, "", url.toString());
  };

  const closeChat = async () => {
    setSelectedChat(null);

    const url = new URL(window.location.href);
    url.searchParams.delete("chat_id");
    url.searchParams.delete("archivio");
    window.history.pushState({}, "", url.toString());

    await loadRequests();
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleCreatedRequest = async (chatId) => {
    setShowCreateModal(false);
    setActiveTab("attive");
    await loadRequests();
    openChat(chatId, false);
  };

  if (selectedChat) {
    return (
      <ChatView
        chatId={selectedChat.id}
        archived={Boolean(selectedChat.archived)}
        onBack={closeChat}
      />
    );
  }

  const showNotificationButton =
    canRequestNotifications() &&
    notificationPermission !== "granted";

  const currentRequests = activeTab === "archivio" ? archivedRequests : requests;

  return (
    <>
      <div className="min-h-[100dvh] flex flex-col" style={{ background: "var(--background)" }}>
        <div className="p-4 pt-6" style={{ background: "var(--background)" }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Ciao, {user?.nome}</h1>
              <p style={{ color: "var(--text-secondary)" }}>Richieste assistenza</p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="flex items-center justify-center gap-2"
                style={{
                  height: 42,
                  padding: "0 14px",
                  borderRadius: 10,
                  background: "#3B82F6",
                  color: "#ffffff",
                  fontWeight: 700,
                  border: "1px solid rgba(59, 130, 246, 0.35)"
                }}
              >
                <Plus className="w-4 h-4" />
                Nuova
              </button>

              <button
                type="button"
                onClick={toggleThemeMode}
                className="p-2"
                data-testid="theme-toggle-btn"
                title={themeMode === "dark" ? "Tema chiaro" : "Tema scuro"}
                style={{
                  color: "var(--text-secondary)"
                }}
              >
                {themeMode === "dark"
                  ? <Sun className="w-6 h-6" />
                  : <Moon className="w-6 h-6" />}
              </button>

              <button
                onClick={handleLogout}
                className="p-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-6 h-6" style={{ color: "#FF3B30" }} />
              </button>
            </div>
          </div>

          {showNotificationButton && (
            <div
              className="mt-4 rounded-2xl p-4"
              style={{
                background: "var(--chat-card)",
                border: "1px solid var(--border)"
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 9999,
                    background: "rgba(37, 211, 102, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}
                >
                  <Bell className="w-5 h-5" style={{ color: "var(--primary)" }} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">Abilita notifiche</div>
                  <div className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
                    Serve per ricevere notifiche push e aggiornare correttamente il badge dell’app operatori.
                  </div>

                  <button
                    type="button"
                    onClick={requestNotificationPermission}
                    disabled={requestingNotificationPermission}
                    className="btn-primary mt-3 flex items-center justify-center gap-2"
                    style={{ minWidth: 180, padding: "12px 18px" }}
                  >
                    {requestingNotificationPermission ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                    Abilita notifiche
                  </button>

                  {notificationPermission === "denied" && (
                    <div className="mt-2 text-sm" style={{ color: "#FF3B30" }}>
                      Hai negato il permesso. Riabilitalo dalle impostazioni della web app su iPhone.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <div
              className="inline-flex rounded-xl p-1"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                width: "100%"
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab("attive")}
                data-testid="tab-attive"
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 14,
                  fontSize: 14,
                  fontWeight: 700,
                  background: activeTab === "attive" ? "#ffffff" : "transparent",
                  color: activeTab === "attive" ? "#111827" : "var(--text-secondary)",
                  boxShadow: activeTab === "attive" ? "0 1px 2px rgba(0,0,0,0.06)" : "none"
                }}
              >
                Attive {requests.length > 0 ? `(${requests.length})` : ""}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("archivio")}
                data-testid="tab-archivio"
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 14,
                  fontSize: 14,
                  fontWeight: 700,
                  background: activeTab === "archivio" ? "#ffffff" : "transparent",
                  color: activeTab === "archivio" ? "#111827" : "var(--text-secondary)",
                  boxShadow: activeTab === "archivio" ? "0 1px 2px rgba(0,0,0,0.06)" : "none"
                }}
              >
                Archivio {archivedRequests.length > 0 ? `(${archivedRequests.length})` : ""}
              </button>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
              <span className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
                Filtri
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <SelectField
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                options={[
                  { value: "default", label: "Mie + non assegnate" },
                  { value: "mie", label: "Solo mie" },
                  { value: "non_assegnate", label: "Non assegnate" },
                  { value: "tutte", label: "Tutte" }
                ]}
              />

              <SelectField
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: "tutte", label: "Tutti gli stati" },
                  { value: "nuova", label: "Nuova" },
                  { value: "in_attesa", label: "In attesa" },
                  { value: "in_lavorazione", label: "In lavorazione" },
                  { value: "completata", label: "Completata" }
                ]}
              />


            </div>
          </div>
        </div>

        <div className="pb-10">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
            </div>
          ) : currentRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              {activeTab === "archivio" ? (
                <Archive className="w-16 h-16 mb-4" style={{ color: "var(--text-muted)" }} />
              ) : (
                <MessageCircle className="w-16 h-16 mb-4" style={{ color: "var(--text-muted)" }} />
              )}

              <h3 className="text-xl font-semibold mb-2">
                {activeTab === "archivio" ? "Nessuna richiesta archiviata" : "Nessuna richiesta trovata"}
              </h3>

              <p style={{ color: "var(--text-secondary)" }}>
                {activeTab === "archivio"
                  ? "Qui troverai le richieste concluse e archiviate."
                  : "Non ci sono richieste che corrispondono ai filtri selezionati."}
              </p>
            </div>
          ) : (
            <div style={{ background: "var(--background)" }}>
              <div
                style={{
                  background: "var(--surface)",
                  borderTop: "1px solid var(--border)",
                  borderBottom: "1px solid var(--border)"
                }}
              >
                {currentRequests.map((request) => {
                  const unreadCount = Number(request.non_letti_operatore ?? 0);
                  const isArchivedTab = activeTab === "archivio";

                  return (
                    <RequestCard
                      key={`${isArchivedTab ? "arch" : "act"}-${request.id}`}
                      request={request}
                      unreadCount={unreadCount}
                      archived={isArchivedTab}
                      onClick={() => openChat(request.id, isArchivedTab)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <NewRequestModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreatedRequest}
      />
    </>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--background)" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--primary)" }} />
      </div>
    );
  }

  if (!user || !isOperatorUser(user)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const { updateAvailable, applyUpdate, dismissUpdate } = useServiceWorkerUpdate();

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen w-screen" style={{ background: "var(--background)" }}>
          {updateAvailable && (
            <ServiceWorkerUpdateBanner
              onUpdate={applyUpdate}
              onDismiss={dismissUpdate}
            />
          )}

          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainApp />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
